import React from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';
import { isPathbuddyHost } from '../../config/eduHost';
import EduPricingPage from '../pricing/EduPricingPage';
import TmuaLandingRoute from '../../app/routes/TmuaLandingRoute';
import LnatLandingRoute from '../../app/routes/LnatLandingRoute';
import UcatLandingRoute from '../../app/routes/UcatLandingRoute';
import TaraLandingRoute from '../../app/routes/TaraLandingRoute';
import EsatLandingRoute from '../../app/routes/EsatLandingRoute';
import OxbridgeLandingRoute from '../../app/routes/OxbridgeLandingRoute';
import MedicalSchoolLandingRoute from '../../app/routes/MedicalSchoolLandingRoute';
import DegreeApprenticeshipLandingRoute from '../../app/routes/DegreeApprenticeshipLandingRoute';
import IvoriMethodLnatRoute from '../../app/routes/IvoriMethodLnatRoute';
import IvoriMethodTmuaRoute from '../../app/routes/IvoriMethodTmuaRoute';
import IvoriMethodUcatRoute from '../../app/routes/IvoriMethodUcatRoute';
import IvoriMethodEsatRoute from '../../app/routes/IvoriMethodEsatRoute';
import IvoriMethodTaraRoute from '../../app/routes/IvoriMethodTaraRoute';
import IvoriMethodOxbridgeRoute from '../../app/routes/IvoriMethodOxbridgeRoute';
import IvoriMethodMedicalSchoolRoute from '../../app/routes/IvoriMethodMedicalSchoolRoute';
import IvoriMethodDegreeApprenticeshipRoute from '../../app/routes/IvoriMethodDegreeApprenticeshipRoute';
import IvoriMethodPathbuddyRoute from '../../app/routes/IvoriMethodPathbuddyRoute';
import LnatFeatureRoute from '../../app/routes/LnatFeatureRoute';
import LnatInsightsRoute from '../../app/routes/LnatInsightsRoute';
import LnatInsightsArticleRoute from '../../app/routes/LnatInsightsArticleRoute';
import ExamCaseStudyRoute from '../../app/routes/ExamCaseStudyRoute';
import { EduRedirect, HostGate } from './HostGate';
import MarketingLayout from './MarketingLayout';
import {
  EXAM_SLUGS,
  INSIGHT_SECTIONS,
  SAME_HOST_REDIRECTS,
  resolveSameHostRedirect,
} from './redirects';

export const lazyPages = {
  HomePage: React.lazy(() => import('./pages/HomePage')),
  PricingPage: React.lazy(() => import('./pages/PricingPage')),
  ExamUseCasePage: React.lazy(() => import('./pages/ExamUseCasePage')),
  FeaturesOverviewPage: React.lazy(() => import('./pages/FeaturesOverviewPage')),
  FeatureCasesPage: React.lazy(() => import('./pages/FeatureCasesPage')),
  FeatureInterviewsPage: React.lazy(() => import('./pages/FeatureInterviewsPage')),
  FeaturePathsPage: React.lazy(() => import('./pages/FeaturePathsPage')),
  FeatureBuilderPage: React.lazy(() => import('./pages/FeatureBuilderPage')),
  MethodOverviewPage: React.lazy(() => import('./pages/MethodOverviewPage')),
  MethodPage: React.lazy(() => import('./pages/MethodPage')),
  InsightsOverviewPage: React.lazy(() => import('./pages/InsightsOverviewPage')),
  InsightsIndexPage: React.lazy(() => import('./pages/InsightsIndexPage')),
  ArticlePage: React.lazy(() => import('./pages/ArticlePage')),
  ForLearnersPage: React.lazy(() => import('./pages/ForLearnersPage')),
  ForParentsPage: React.lazy(() => import('./pages/ForParentsPage')),
  ForTeachersPage: React.lazy(() => import('./pages/ForTeachersPage')),
  ForSchoolsPage: React.lazy(() => import('./pages/ForSchoolsPage')),
  SchoolCasePage: React.lazy(() => import('./pages/SchoolCasePage')),
  LegalPage: React.lazy(() => import('./pages/LegalPage')),
};

const EXAM_LEGACY = {
  lnat: <LnatLandingRoute />,
  tmua: <TmuaLandingRoute />,
  tara: <TaraLandingRoute />,
  ucat: <UcatLandingRoute />,
  esat: <EsatLandingRoute />,
  oxbridge: <OxbridgeLandingRoute />,
  'degree-apprenticeships': <DegreeApprenticeshipLandingRoute />,
};

const ALIAS_LEGACY = {
  '/lnat-prep': <LnatLandingRoute />,
  '/specialty/lnat': <LnatLandingRoute />,
  '/tmua-prep': <TmuaLandingRoute />,
  '/specialty/tmua': <TmuaLandingRoute />,
  '/ucat-prep': <UcatLandingRoute />,
  '/specialty/ucat': <UcatLandingRoute />,
  '/tara-prep': <TaraLandingRoute />,
  '/specialty/tara': <TaraLandingRoute />,
  '/esat-prep': <EsatLandingRoute />,
  '/specialty/esat': <EsatLandingRoute />,
  '/oxbridge-prep': <OxbridgeLandingRoute />,
  '/specialty/oxbridge': <OxbridgeLandingRoute />,
  '/medicalschool': <MedicalSchoolLandingRoute />,
  '/medicalschool-prep': <MedicalSchoolLandingRoute />,
  '/specialty/medicalschool': <MedicalSchoolLandingRoute />,
  '/degreeapprenticeship': <DegreeApprenticeshipLandingRoute />,
  '/degreeapprenticeship-prep': <DegreeApprenticeshipLandingRoute />,
  '/specialty/degreeapprenticeship': <DegreeApprenticeshipLandingRoute />,
  '/degree-apprenticeships-prep': <DegreeApprenticeshipLandingRoute />,
  '/specialty/degree-apprenticeships': <DegreeApprenticeshipLandingRoute />,
  '/method/medicalschool': <IvoriMethodMedicalSchoolRoute />,
  '/method/degreeapprenticeship': <IvoriMethodDegreeApprenticeshipRoute />,
  '/method/pathbuddy': <IvoriMethodPathbuddyRoute />,
  '/features/chat': <LnatFeatureRoute />,
  '/features/fluid': <LnatFeatureRoute />,
  '/features/studio': <LnatFeatureRoute />,
  '/features/apps': <LnatFeatureRoute />,
  '/case-studies/handsworth-grammar': <ExamCaseStudyRoute />,
  '/case-studies/clifton-college': <ExamCaseStudyRoute />,
};

const METHOD_LEGACY = {
  lnat: <IvoriMethodLnatRoute />,
  tmua: <IvoriMethodTmuaRoute />,
  ucat: <IvoriMethodUcatRoute />,
  esat: <IvoriMethodEsatRoute />,
  tara: <IvoriMethodTaraRoute />,
  oxbridge: <IvoriMethodOxbridgeRoute />,
};

const EXACT_PATHS = new Set([
  '/',
  '/pricing',
  '/lnat',
  '/tmua',
  '/tara',
  '/ucat',
  '/esat',
  '/oxbridge',
  '/medicine',
  '/dentistry',
  '/degree-apprenticeships',
  '/method',
  '/method/lnat',
  '/method/tmua',
  '/method/tara',
  '/method/ucat',
  '/method/esat',
  '/method/oxbridge',
  '/method/medicine',
  '/method/degree-apprenticeships',
  '/insights',
  '/insights/lnat',
  '/insights/tmua',
  '/insights/tara',
  '/insights/ucat',
  '/insights/esat',
  '/insights/oxbridge',
  '/insights/medicine',
  '/insights/degree-apprenticeships',
  '/insights/general',
  '/features',
  '/features/cases',
  '/features/interviews',
  '/features/paths',
  '/features/builder',
  '/for-learners',
  '/for-parents',
  '/for-teachers',
  '/for-schools',
  '/for-schools/handsworth',
  '/for-schools/clifton',
]);

export const ROUTE_TEMPLATES = [
  'EduHeader.dc.html',
  'EduFooter.dc.html',
  'ExamTpl.dc.html',
  'UseCaseTpl.dc.html',
  'MethodTpl.dc.html',
  'InsightsTpl.dc.html',
  'ArticleTpl.dc.html',
  'FeatureTpl.dc.html',
];

export function isMarketingRoute(pathname) {
  if (EXACT_PATHS.has(pathname)) return true;
  const parts = String(pathname || '').split('/').filter(Boolean);
  return parts.length === 3 && parts[0] === 'insights' && INSIGHT_SECTIONS.indexOf(parts[1]) !== -1;
}

function MethodExam() {
  const { exam } = useParams();
  const legacy = METHOD_LEGACY[exam] || null;
  if (isPathbuddyHost()) {
    if (legacy) return legacy;
    return <Navigate to="/" replace />;
  }
  return (
    <MarketingLayout>
      <lazyPages.MethodPage />
    </MarketingLayout>
  );
}

function InsightsOneSegment() {
  const { slug } = useParams();
  if (isPathbuddyHost()) return <LnatInsightsArticleRoute />;
  if (INSIGHT_SECTIONS.indexOf(slug) !== -1) {
    return (
      <MarketingLayout>
        <lazyPages.InsightsIndexPage exam={slug} />
      </MarketingLayout>
    );
  }
  const to = resolveSameHostRedirect(`/insights/${slug}`);
  return <Navigate to={to || '/insights'} replace />;
}

function ArticleRoute() {
  if (isPathbuddyHost()) return <Navigate to="/" replace />;
  return (
    <MarketingLayout>
      <lazyPages.ArticlePage />
    </MarketingLayout>
  );
}

function FeatureSlug() {
  const { slug } = useParams();
  if (isPathbuddyHost()) return <LnatFeatureRoute />;
  const to = resolveSameHostRedirect(`/features/${slug}`);
  return <Navigate to={to || '/features'} replace />;
}

function CaseStudySlug() {
  const { slug } = useParams();
  if (isPathbuddyHost()) return <ExamCaseStudyRoute />;
  const to = resolveSameHostRedirect(`/case-studies/${slug}`);
  return <Navigate to={to || '/for-schools'} replace />;
}

export function marketingRoutes() {
  const { PricingPage } = lazyPages;
  const routes = [
    <Route
      key="pricing"
      path="/pricing"
      element={<HostGate legacy={<EduPricingPage />}><PricingPage /></HostGate>}
    />,
    <Route
      key="upgrade"
      path="/upgrade"
      element={<HostGate legacy={<EduPricingPage />}><PricingPage /></HostGate>}
    />,
    <Route
      key="plans"
      path="/plans"
      element={<HostGate legacy={<EduPricingPage />}><PricingPage /></HostGate>}
    />,
  ];

  EXAM_SLUGS.forEach((slug) => {
    const Page = lazyPages.ExamUseCasePage;
    routes.push(
      <Route
        key={slug}
        path={`/${slug}`}
        element={(
          <HostGate legacy={EXAM_LEGACY[slug] || null}>
            <Page exam={slug} />
          </HostGate>
        )}
      />
    );
  });

  routes.push(
    <Route
      key="dentistry"
      path="/dentistry"
      element={(
        <HostGate legacy={null}>
          <lazyPages.ExamUseCasePage exam="dentistry" />
        </HostGate>
      )}
    />
  );

  SAME_HOST_REDIRECTS.forEach((row) => {
    routes.push(
      <Route
        key={row.path}
        path={row.path}
        element={<EduRedirect to={row.to} legacy={ALIAS_LEGACY[row.path] || null} />}
      />
    );
  });

  routes.push(
    <Route
      key="method"
      path="/method"
      element={<HostGate legacy={null}><lazyPages.MethodOverviewPage /></HostGate>}
    />,
    <Route key="method-exam" path="/method/:exam" element={<MethodExam />} />,
    <Route
      key="insights"
      path="/insights"
      element={<HostGate legacy={<LnatInsightsRoute />}><lazyPages.InsightsOverviewPage /></HostGate>}
    />,
    <Route key="article" path="/insights/:exam/:slug" element={<ArticleRoute />} />,
    <Route key="insights-one" path="/insights/:slug" element={<InsightsOneSegment />} />,
    <Route
      key="features"
      path="/features"
      element={<HostGate legacy={null}><lazyPages.FeaturesOverviewPage /></HostGate>}
    />,
    <Route
      key="features-cases"
      path="/features/cases"
      element={<HostGate legacy={<LnatFeatureRoute />}><lazyPages.FeatureCasesPage /></HostGate>}
    />,
    <Route
      key="features-interviews"
      path="/features/interviews"
      element={<HostGate legacy={null}><lazyPages.FeatureInterviewsPage /></HostGate>}
    />,
    <Route
      key="features-paths"
      path="/features/paths"
      element={<HostGate legacy={null}><lazyPages.FeaturePathsPage /></HostGate>}
    />,
    <Route
      key="features-builder"
      path="/features/builder"
      element={<HostGate legacy={null}><lazyPages.FeatureBuilderPage /></HostGate>}
    />,
    <Route key="features-slug" path="/features/:slug" element={<FeatureSlug />} />,
    <Route
      key="for-learners"
      path="/for-learners"
      element={<HostGate legacy={null}><lazyPages.ForLearnersPage /></HostGate>}
    />,
    <Route
      key="for-parents"
      path="/for-parents"
      element={<HostGate legacy={null}><lazyPages.ForParentsPage /></HostGate>}
    />,
    <Route
      key="for-teachers"
      path="/for-teachers"
      element={<HostGate legacy={null}><lazyPages.ForTeachersPage /></HostGate>}
    />,
    <Route
      key="for-schools"
      path="/for-schools"
      element={<HostGate legacy={null}><lazyPages.ForSchoolsPage /></HostGate>}
    />,
    <Route
      key="handsworth"
      path="/for-schools/handsworth"
      element={<HostGate legacy={null}><lazyPages.SchoolCasePage slug="handsworth" /></HostGate>}
    />,
    <Route
      key="clifton"
      path="/for-schools/clifton"
      element={<HostGate legacy={null}><lazyPages.SchoolCasePage slug="clifton" /></HostGate>}
    />,
    <Route key="case-slug" path="/case-studies/:slug" element={<CaseStudySlug />} />
  );

  return routes;
}
